import React, { useEffect, useRef } from "react";
import { onLevel } from "@/lib/orbLevel";
import { onSceneCue } from "@/lib/sceneBus";

/* Background scene that breathes with the REAL assistant state:
   idle → almost still sparse grid + slow dust · listening/speaking → waves driven by the real
   mic/playback level (same bus as the orb) · thinking → light flow for the actual waiting time ·
   real tool start → one scan pass · real completion → one confirm ring · real failure → one red flash.
   Everything is transform/opacity only, one rAF writing CSS variables (no per-frame React state). */
const INTENSITY = { idle: 0.3, wake_listening: 0.45, listening: 0.7, thinking: 0.85, speaking: 0.75,
  executing: 0.9, mic_off: 0.12, disconnected: 0.08 };
const CUE_CLASS = { scan: "cue-scan", ok: "cue-ok", error: "cue-error" };
const CUE_MS = { scan: 1400, ok: 900, error: 700 };
const reduced = () => typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || document.documentElement.dataset.reduceMotion === "1");

export const SceneBackdrop = ({ state = "idle", dimmed = false }) => {
  const ref = useRef(null);
  const a = useRef({ lvl: 0, target: 0, int: 0.3, intTarget: 0.3, t: 0 });

  useEffect(() => { a.current.intTarget = (INTENSITY[state] ?? 0.3) * (dimmed ? 0.45 : 1); }, [state, dimmed]);

  useEffect(() => { // real audio level only while there is real audio in or out
    if (!["listening", "wake_listening", "speaking"].includes(state)) { a.current.target = 0; return; }
    return onLevel((v) => { a.current.target = v; });
  }, [state]);

  useEffect(() => onSceneCue(({ type, motif }) => {
    const el = ref.current, cls = CUE_CLASS[type];
    if (!el || !cls) return;
    if (type === "scan") el.dataset.motif = motif || "";
    el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls);
    clearTimeout(el._cueT);
    el._cueT = setTimeout(() => { el.classList.remove(cls); if (type === "scan") el.dataset.motif = ""; }, CUE_MS[type]);
  }), []);

  useEffect(() => {
    if (reduced()) return; // static backdrop: no loop, no load
    let raf, last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const s = a.current;
      s.lvl += (s.target - s.lvl) * Math.min(1, dt * (s.target > s.lvl ? 12 : 4));
      s.int += (s.intTarget - s.int) * Math.min(1, dt * 2);
      s.t += dt;
      const el = ref.current;
      if (el) {
        el.style.setProperty("--lvl", s.lvl.toFixed(3));
        el.style.setProperty("--int", s.int.toFixed(3));
        el.style.setProperty("--gx", `${(Math.sin(s.t * 0.055) * 16).toFixed(2)}px`);
        el.style.setProperty("--gy", `${(Math.cos(s.t * 0.04) * 12).toFixed(2)}px`);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={ref} className="scene-bd" data-state={state} data-dimmed={dimmed ? "1" : "0"}
      aria-hidden="true" data-testid="scene-backdrop">
      <div className="scene-grid" />
      <div className="scene-dust" />
      <div className="scene-listen" />
      <div className="scene-think" />
      <div className="scene-speak" />
      <div className="scene-scan" />
      <div className="scene-motif" />
      <div className="scene-ring" />
    </div>
  );
};

export default SceneBackdrop;
